import type { ExchangeBlockerClassification, ExchangeFreshnessState, ExchangeRunnerWaitState } from '../api/exchange-sync';
import type { TransactionsExchangeProvenance } from '../api/transactions';
import { formatExchangeFreshnessAge } from './exchangeHealth';
import {
  getExchangeFreshnessRecoveryLabel,
  getExchangeFreshnessRecoveryVerdictKey,
  type ExchangeRecoveryVerdictKey,
} from './exchangeFreshnessRecovery';
import {
  AUTH_SESSION_BLOCKED_MESSAGE,
  SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE,
  formatSourceProofOccurredAt,
} from './sourceProof';
import type { SetupHandoffContext } from './setupHandoff';
import type { TaxSourceProofSnapshot } from './taxSourceProof';

export type SourceFreshnessOwnerRoute = '/import' | '/exchange-sync' | '/transactions';
export type SourceFreshnessKind = 'csv-import' | 'api-sync';

export interface SourceFreshnessSnapshot {
  ownerRoute: SourceFreshnessOwnerRoute;
  sourceKind: SourceFreshnessKind;
  sourceLabel: string;
  proofOccurredAt: string | null;
  importedCount: number | null;
  skippedCount: number | null;
  errorCount: number | null;
  matchedCount: number | null;
  freshnessState?: ExchangeFreshnessState | null;
  freshnessStaleHours?: number | null;
  blockerCode?: string | null;
  blockerClassification?: ExchangeBlockerClassification;
  runnerWaitState?: ExchangeRunnerWaitState;
  blockerDetail?: string | null;
}

export interface SourceFreshnessHelper {
  tone: 'info' | 'warning';
  eyebrow: string;
  message: string;
  meta?: string;
}

export function getSourceFreshnessStorageKey(userId: string) {
  return `source-freshness-handoff:${userId}`;
}

function toNullableNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isOwnerRoute(value: unknown): value is SourceFreshnessOwnerRoute {
  return value === '/import' || value === '/exchange-sync' || value === '/transactions';
}

function isSourceKind(value: unknown): value is SourceFreshnessKind {
  return value === 'csv-import' || value === 'api-sync';
}

export function readSourceFreshnessHandoff(userId?: string | null): SourceFreshnessSnapshot | null {
  if (!userId || typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(getSourceFreshnessStorageKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<SourceFreshnessSnapshot>;
    if (!isOwnerRoute(parsed.ownerRoute) || !isSourceKind(parsed.sourceKind) || typeof parsed.sourceLabel !== 'string' || !parsed.sourceLabel.trim()) {
      return null;
    }

    return {
      ownerRoute: parsed.ownerRoute,
      sourceKind: parsed.sourceKind,
      sourceLabel: parsed.sourceLabel,
      proofOccurredAt: typeof parsed.proofOccurredAt === 'string' ? parsed.proofOccurredAt : null,
      importedCount: toNullableNumber(parsed.importedCount),
      skippedCount: toNullableNumber(parsed.skippedCount),
      errorCount: toNullableNumber(parsed.errorCount),
      matchedCount: toNullableNumber(parsed.matchedCount),
      freshnessState: parsed.freshnessState ?? null,
      freshnessStaleHours: toNullableNumber(parsed.freshnessStaleHours),
      blockerCode: typeof parsed.blockerCode === 'string' ? parsed.blockerCode : null,
      blockerClassification: parsed.blockerClassification ?? null,
      runnerWaitState: parsed.runnerWaitState ?? null,
      blockerDetail: typeof parsed.blockerDetail === 'string' ? parsed.blockerDetail : null,
    };
  } catch {
    return null;
  }
}

export function writeSourceFreshnessHandoff(
  userId: string | null | undefined,
  snapshot: SourceFreshnessSnapshot | null,
) {
  if (!userId || typeof window === 'undefined') return;

  try {
    const storageKey = getSourceFreshnessStorageKey(userId);
    if (!snapshot) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }

    window.sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    // noop
  }
}

export function buildSourceFreshnessFromSetupHandoff(
  handoff: SetupHandoffContext | null | undefined,
): SourceFreshnessSnapshot | null {
  if (!handoff || (handoff.kind !== 'csv-import' && handoff.kind !== 'api-sync')) {
    return null;
  }

  return {
    ownerRoute: handoff.ownerRoute ?? (handoff.kind === 'csv-import' ? '/import' : '/exchange-sync'),
    sourceKind: handoff.kind,
    sourceLabel: handoff.kind === 'csv-import' ? handoff.sourceLabel : handoff.exchangeLabel,
    proofOccurredAt: handoff.occurredAt,
    importedCount: handoff.imported,
    skippedCount: handoff.skipped,
    errorCount: handoff.errors,
    matchedCount: null,
    freshnessState: handoff.kind === 'api-sync' ? handoff.freshnessState ?? null : 'healthy',
    freshnessStaleHours: handoff.kind === 'api-sync' ? handoff.freshnessStaleHours ?? null : null,
    blockerCode: handoff.kind === 'api-sync' ? handoff.blocker ?? null : null,
    blockerClassification: handoff.kind === 'api-sync' ? handoff.blockerClassification ?? null : null,
    runnerWaitState: handoff.kind === 'api-sync' ? handoff.runnerWaitState ?? null : null,
    blockerDetail: handoff.kind === 'api-sync' ? handoff.blockerDetail ?? null : null,
  };
}

export function buildSourceFreshnessFromTransactionsProvenance(
  sourceLabel: string,
  proofOccurredAt: string | null,
  counts: {
    imported: number | null | undefined;
    skipped: number | null | undefined;
    errors: number | null | undefined;
    matchedCount: number | null | undefined;
  },
  provenance?: TransactionsExchangeProvenance | null,
): SourceFreshnessSnapshot {
  if (!provenance) {
    return {
      ownerRoute: '/import',
      sourceKind: 'csv-import',
      sourceLabel,
      proofOccurredAt,
      importedCount: counts.imported ?? null,
      skippedCount: counts.skipped ?? null,
      errorCount: counts.errors ?? null,
      matchedCount: counts.matchedCount ?? null,
      freshnessState: 'healthy',
      freshnessStaleHours: null,
      blockerCode: null,
      blockerClassification: null,
      runnerWaitState: null,
      blockerDetail: null,
    };
  }

  return {
    ownerRoute: '/exchange-sync',
    sourceKind: 'api-sync',
    sourceLabel: provenance.sourceLabel || sourceLabel,
    proofOccurredAt: provenance.lastVerifiedSyncAt ?? proofOccurredAt,
    importedCount: counts.imported ?? null,
    skippedCount: counts.skipped ?? null,
    errorCount: counts.errors ?? null,
    matchedCount: counts.matchedCount ?? null,
    freshnessState: provenance.freshnessState ?? null,
    freshnessStaleHours: provenance.freshnessStaleHours ?? null,
    blockerCode: provenance.blockerCode ?? null,
    blockerClassification: provenance.blockerClassification ?? null,
    runnerWaitState:
      provenance.safeRetryGuidance.code === 'wait_for_runner_window'
        ? 'runner_limited_wait'
        : null,
    blockerDetail: provenance.safeRetryGuidance.code && provenance.safeRetryGuidance.code !== 'none'
      ? provenance.safeRetryGuidance.code
      : null,
  };
}

export function buildSourceFreshnessFromTaxSourceProof(
  proof: TaxSourceProofSnapshot | null | undefined,
): SourceFreshnessSnapshot | null {
  if (!proof) return null;

  return {
    ownerRoute: proof.ownerSurface,
    sourceKind: proof.sourceKind ?? 'csv-import',
    sourceLabel: proof.sourceLabel,
    proofOccurredAt: proof.importedAt,
    importedCount: proof.importedCount,
    skippedCount: proof.skippedCount,
    errorCount: proof.errorCount,
    matchedCount: proof.matchedCount,
    freshnessState: proof.freshnessState ?? (proof.sourceKind === 'api-sync' ? null : 'healthy'),
    freshnessStaleHours: proof.freshnessStaleHours ?? null,
    blockerCode: proof.blockerCode ?? null,
    blockerClassification: proof.blockerClassification ?? null,
    runnerWaitState: proof.runnerWaitState ?? null,
    blockerDetail: proof.blockerDetail ?? null,
  };
}

function getExchangeVerdict(snapshot: SourceFreshnessSnapshot): ExchangeRecoveryVerdictKey {
  if (snapshot.blockerCode === 'AUTH_SESSION_BLOCKED') {
    return 'provider_auth_repair';
  }

  if (snapshot.blockerCode === 'SAFE_EXCHANGE_REPLAY_BLOCKED') {
    return snapshot.runnerWaitState ? 'runner_wait' : 'provider_replay_window';
  }

  return getExchangeFreshnessRecoveryVerdictKey({
    healthState:
      snapshot.freshnessState === 'never_synced'
        ? 'never_synced'
        : snapshot.blockerClassification
          ? 'blocked'
          : snapshot.freshnessState === 'stale_verified'
            ? 'stale'
            : 'healthy',
    freshnessState: snapshot.freshnessState ?? null,
    freshnessBucket:
      snapshot.freshnessState === 'never_synced'
        ? 'never_synced'
        : snapshot.freshnessState === 'stale_verified'
          ? 'stale'
          : 'fresh',
    hasProof: Boolean(snapshot.proofOccurredAt),
    blockerClassification: snapshot.blockerClassification ?? null,
    credentialActionRequired: snapshot.blockerClassification === 'credential_action',
    providerAuthActionRequired:
      snapshot.blockerClassification === 'auth_action' || snapshot.blockerCode === 'AUTH_SESSION_BLOCKED',
    providerReplayBlocked:
      snapshot.blockerClassification === 'safe_exchange_replay_blocked'
      || snapshot.blockerCode === 'SAFE_EXCHANGE_REPLAY_BLOCKED',
    runnerWaitState: snapshot.runnerWaitState ?? null,
  });
}

export function getSourceFreshnessTone(snapshot: SourceFreshnessSnapshot): 'info' | 'success' | 'warning' {
  if (snapshot.sourceKind === 'csv-import') {
    return (snapshot.errorCount ?? 0) > 0 ? 'warning' : 'success';
  }

  const verdict = getExchangeVerdict(snapshot);
  return verdict === 'current_again' ? 'success' : verdict === 'runner_wait' || verdict === 'never_synced' ? 'info' : 'warning';
}

export function getSourceFreshnessVerdictLabel(snapshot: SourceFreshnessSnapshot): string {
  if (snapshot.sourceKind === 'csv-import') {
    return (snapshot.errorCount ?? 0) > 0 ? 'Imported with follow-up' : 'Import complete';
  }

  return getExchangeFreshnessRecoveryLabel({
    healthState:
      snapshot.freshnessState === 'never_synced'
        ? 'never_synced'
        : snapshot.blockerClassification
          ? 'blocked'
          : snapshot.freshnessState === 'stale_verified'
            ? 'stale'
            : 'healthy',
    freshnessState: snapshot.freshnessState ?? null,
    freshnessBucket:
      snapshot.freshnessState === 'never_synced'
        ? 'never_synced'
        : snapshot.freshnessState === 'stale_verified'
          ? 'stale'
          : 'fresh',
    hasProof: Boolean(snapshot.proofOccurredAt),
    blockerClassification: snapshot.blockerClassification ?? null,
    credentialActionRequired: snapshot.blockerClassification === 'credential_action',
    providerAuthActionRequired:
      snapshot.blockerClassification === 'auth_action' || snapshot.blockerCode === 'AUTH_SESSION_BLOCKED',
    providerReplayBlocked:
      snapshot.blockerClassification === 'safe_exchange_replay_blocked'
      || snapshot.blockerCode === 'SAFE_EXCHANGE_REPLAY_BLOCKED',
    runnerWaitState: snapshot.runnerWaitState ?? null,
  });
}

export function getSourceFreshnessAgeLabel(snapshot: SourceFreshnessSnapshot): string | null {
  if (snapshot.sourceKind !== 'api-sync') return null;
  return formatExchangeFreshnessAge(snapshot.freshnessStaleHours);
}

export function getSourceFreshnessSummary(
  snapshot: SourceFreshnessSnapshot,
  decisionRoute: '/transactions' | '/goals' | '/rebalancing' | '/tax-optimisation',
) {
  const ownerRouteLabel = snapshot.ownerRoute;
  const freshnessAge = getSourceFreshnessAgeLabel(snapshot);

  if (snapshot.sourceKind === 'csv-import') {
    return `Pinecurl is carrying the latest import proof from ${ownerRouteLabel} into ${decisionRoute}. The mounted proof remains trustworthy while ${decisionRoute} keeps the decision-owned next step.`;
  }

  const verdict = getExchangeVerdict(snapshot);
  if (verdict === 'stale_verified') {
    return `Pinecurl is carrying the latest exchange freshness proof from ${ownerRouteLabel} into ${decisionRoute}. The mounted proof remains trustworthy${freshnessAge ? ` at ${freshnessAge}` : ''} while ${decisionRoute} keeps the decision-owned next step.`;
  }

  if (verdict === 'provider_auth_repair') {
    return `Pinecurl is carrying the last trustworthy exchange proof from ${ownerRouteLabel} into ${decisionRoute} while the refresh caveat stays local to this proof rail.`;
  }

  if (verdict === 'provider_credential_repair' || verdict === 'provider_replay_window' || verdict === 'runner_wait') {
    return `Pinecurl is carrying the last trustworthy exchange proof from ${ownerRouteLabel} into ${decisionRoute} while the replay caveat stays local to this proof rail.`;
  }

  return `Pinecurl is carrying the latest exchange freshness proof from ${ownerRouteLabel} into ${decisionRoute}. The mounted proof remains trustworthy while ${decisionRoute} keeps the decision-owned next step.`;
}

export function getSourceFreshnessHelper(
  snapshot: SourceFreshnessSnapshot,
  decisionRoute: '/transactions' | '/goals' | '/rebalancing' | '/tax-optimisation',
): SourceFreshnessHelper | null {
  if (snapshot.sourceKind === 'csv-import') {
    return null;
  }

  const verdict = getExchangeVerdict(snapshot);
  const proofTime = formatSourceProofOccurredAt(snapshot.proofOccurredAt);

  if (verdict === 'stale_verified') {
    return {
      tone: 'warning',
      eyebrow: 'Stale but truthful',
      message: `The carried exchange proof remains trustworthy on ${decisionRoute}, but it is older than Pinecurl’s freshness target${getSourceFreshnessAgeLabel(snapshot) ? ` (${getSourceFreshnessAgeLabel(snapshot)})` : ''}.`,
      meta: [proofTime ? `Proof time ${proofTime}` : null, getSourceFreshnessAgeLabel(snapshot)].filter(Boolean).join(' · ') || undefined,
    };
  }

  if (verdict === 'provider_auth_repair') {
    return {
      tone: 'warning',
      eyebrow: 'Sign in required',
      message: `The carried exchange proof remains trustworthy on ${decisionRoute}, and ${AUTH_SESSION_BLOCKED_MESSAGE}.`,
      meta: [snapshot.blockerCode ?? 'AUTH_SESSION_BLOCKED', snapshot.blockerDetail].filter(Boolean).join(' · ') || undefined,
    };
  }

  if (verdict === 'provider_credential_repair') {
    return {
      tone: 'warning',
      eyebrow: 'Repair credentials',
      message: `The carried exchange proof remains trustworthy on ${decisionRoute}, but Pinecurl needs repaired read-only credentials or permissions before live replay can continue.`,
      meta: [snapshot.blockerCode, snapshot.blockerDetail].filter(Boolean).join(' · ') || undefined,
    };
  }

  if (verdict === 'provider_replay_window' || verdict === 'runner_wait') {
    return {
      tone: verdict === 'runner_wait' ? 'info' : 'warning',
      eyebrow: getSourceFreshnessVerdictLabel(snapshot),
      message: `The carried exchange proof remains trustworthy on ${decisionRoute}, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`,
      meta: [snapshot.blockerCode, snapshot.blockerDetail].filter(Boolean).join(' · ') || undefined,
    };
  }

  return null;
}
